/**
 * Co-op stats — the billing-grade counts report screen (task 007-03, ADR-010
 * §8; task 035-08 makes it the Co-op stats view of epic 035).
 *
 * Month presets + custom range, advertiser/campaign/offer-type/status filters,
 * a campaign|advertiser grain switch, the "Show test leads" toggle (off by
 * default, FR-B29), her column set with per-row Unique / All exports, the
 * editor link, the partner line and client-side sorting, plus the screen-level
 * Export CSV for the same filters.
 *
 * Zero-count cells render empty, not "0" (Q63 §7.3) — a rendering rule of this
 * screen; the api keeps the numbers. Talk to the api through the screen-local
 * `reports-api.ts` (A2 (b)) and the shared `api` client for the two dropdown
 * loaders only.
 *
 * Task 035-01 moved this screen under the Reporting shell (`ReportShell`) and
 * onto the shared filter/table components in `report-filters.tsx` /
 * `report-table.tsx`. Task 035-08 keeps the style filter pinned to Co-op by
 * default (one view per style — the *All offer types* option widens it), adds
 * the campaign-dates / lead-limit / exports columns and the partner line, and
 * relabels `Sent` → *Total leads* and `Accepted` → *Partner accepted* (README
 * §Decisions pre-resolved, Co-op — the help strings there are verbatim; no
 * rendered string names an ADR id).
 */

import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AdvertiserResponse, CampaignStatus } from '@leads-platform/shared';
import { campaignStatusEnum, campaignStyleEnum } from '@leads-platform/shared';
import { api, type CampaignDetailResponse } from '../../lib/api';
import { campaignStyleEnumToLabel } from '../../lib/campaign-styles';
import { fmtCount, fmtDate, fmtMoney } from '../../lib/format';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Select } from '../../components/ui/select';
import { Typeahead } from '../../components/typeahead';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  fetchCoopCounts,
  coopLeadsExportUrl,
  type CampaignStyleValue,
  type CoopCountsFilters,
  type CoopCountsGroup,
  type CoopCountsResponse,
  type CoopCountsRow,
  type CoopCountsTotals,
  type CoopLeadsVariant,
} from './reports-api';
import { ReportShell } from './report-shell';
import {
  RangeFilters,
  ReportSearch,
  SearchNote,
  TestLeadsToggle,
  matchesSearch,
  useReportRange,
  useSharedReportFilters,
} from './report-filters';
import {
  cell,
  EmptyRow,
  ErrorBanner,
  LoadingRow,
  SortableHeader,
  UNIQUE_TOTAL_TITLE,
  useClientSort,
} from './report-table';

type ColumnKey =
  | 'advertiser'
  | 'campaign'
  | 'style'
  | 'campaignDates'
  | 'cpl'
  | 'leadLimit'
  | 'sent'
  | 'unique'
  | 'rejected'
  | 'revenue'
  | 'status'
  | 'accepted'
  | 'duplicates'
  | 'errors'
  | 'skipped'
  | 'exports';

interface Column {
  key: ColumnKey;
  label: string;
  numeric?: boolean;
  /** Column help on the header's `title` — her language, verbatim (no ADR ids). */
  help?: string;
}

/** Header help, verbatim from README §Decisions pre-resolved (Co-op). */
const SENT_HELP =
  'Send attempts that left the platform — a re-send of the same address counts again; Unique is the billable distinct-address count';
const UNIQUE_HELP =
  'Distinct addresses billed — a request left the platform for this campaign, whatever the partner answered, including replies that errored or timed out; a duplicate is excluded where Skip Duplicates is on';
const ACCEPTED_HELP = 'What the partner answered — not the billing basis';

/** Her column set, in her order (README §Decisions pre-resolved, Co-op). */
const CAMPAIGN_COLUMNS: Column[] = [
  { key: 'advertiser', label: 'Advertiser' },
  { key: 'campaign', label: 'Campaign' },
  { key: 'style', label: 'Offer Type' },
  { key: 'campaignDates', label: 'Campaign dates' },
  { key: 'cpl', label: 'CPL', numeric: true },
  { key: 'leadLimit', label: 'Lead limit', numeric: true },
  { key: 'sent', label: 'Total leads', numeric: true, help: SENT_HELP },
  { key: 'unique', label: 'Unique', numeric: true, help: UNIQUE_HELP },
  { key: 'rejected', label: 'Rejected', numeric: true },
  { key: 'revenue', label: 'Revenue', numeric: true },
  { key: 'status', label: 'Status' },
  { key: 'accepted', label: 'Partner accepted', numeric: true, help: ACCEPTED_HELP },
  { key: 'duplicates', label: 'Duplicates', numeric: true },
  { key: 'errors', label: 'Errors', numeric: true },
  { key: 'skipped', label: 'Skipped', numeric: true },
  { key: 'exports', label: 'Exports' },
];

/**
 * The advertiser grain keeps 007's behaviour: the campaign-attribute columns
 * drop with `Campaign` — the partner line rides the Campaign cell, and the
 * per-row export buttons are the `exports` column, so dropping the column is
 * what "hidden on the advertiser grain" means.
 */
const ADVERTISER_GRAIN_HIDDEN: readonly ColumnKey[] = [
  'campaign',
  'style',
  'status',
  'cpl',
  'campaignDates',
  'leadLimit',
  'exports',
];
const ADVERTISER_COLUMNS: Column[] = CAMPAIGN_COLUMNS.filter(
  (c) => !ADVERTISER_GRAIN_HIDDEN.includes(c.key)
);

/** One row cell as text — the only place a column key is read off a row. */
function cellFor(row: CoopCountsRow, key: ColumnKey): string {
  switch (key) {
    case 'advertiser':
      return row.advertiserName;
    case 'campaign':
      return row.campaignName ?? '';
    case 'style':
      return row.campaignStyle ? campaignStyleEnumToLabel(row.campaignStyle) : '';
    case 'campaignDates':
      return row.startDate && row.endDate
        ? `${fmtDate(row.startDate)} – ${fmtDate(row.endDate)}`
        : '';
    case 'cpl':
      return fmtMoney(row.pricePerLead);
    case 'leadLimit':
      return limitCell(row.limitValue, row.dailyCap);
    case 'sent':
      return cell(row.sent);
    case 'unique':
      return cell(row.uniqueLeads);
    case 'rejected':
      return cell(row.rejected);
    case 'revenue':
      return fmtMoney(row.revenue);
    case 'status':
      return row.campaignStatus ?? '';
    case 'accepted':
      return cell(row.accepted);
    case 'duplicates':
      return cell(row.duplicates);
    case 'errors':
      return cell(row.errors);
    case 'skipped':
      return cell(row.skipped);
    case 'exports':
      return '';
  }
}

const LIMIT_TYPE_LABELS: Record<'total_clicks' | 'unique_leads' | 'unique_clicks', string> = {
  total_clicks: 'Total clicks',
  unique_leads: 'Unique leads',
  unique_clicks: 'Unique clicks',
};

/** The limit kind as the cell's help — the Custom Opt-in cell's label map, never the raw enum. */
function limitTypeLabel(limitType: CoopCountsRow['limitType']): string | undefined {
  return limitType === null ? undefined : LIMIT_TYPE_LABELS[limitType];
}

/** `500 / day 50` with the cap set (exact spacing), `500` without one, empty with no limit. */
function limitCell(limitValue: number | null, dailyCap: number | null): string {
  if (limitValue === null) return '';
  if (dailyCap === null) return fmtCount(limitValue);
  return `${fmtCount(limitValue)} / day ${fmtCount(dailyCap)}`;
}

/** Sort accessors — client-side over the fetched rows (hundreds at most). */
function rowAccessor(row: CoopCountsRow, key: ColumnKey): string | number | null {
  switch (key) {
    case 'advertiser':
      return row.advertiserName;
    case 'campaign':
      return row.campaignName;
    case 'style':
      return row.campaignStyle;
    case 'campaignDates':
      return row.startDate;
    case 'cpl':
      return row.pricePerLead;
    case 'leadLimit':
      return row.limitValue;
    case 'sent':
      return row.sent;
    case 'unique':
      return row.uniqueLeads;
    case 'rejected':
      return row.rejected;
    case 'revenue':
      return row.revenue;
    case 'status':
      return row.campaignStatus;
    case 'accepted':
      return row.accepted;
    case 'duplicates':
      return row.duplicates;
    case 'errors':
      return row.errors;
    case 'skipped':
      return row.skipped;
    case 'exports':
      return null;
  }
}

/**
 * `<option>` values come back as plain strings — narrow them back to the enum
 * unions through the runtime enums themselves (conventions §Type-safety: no
 * casts; an unknown value means "off").
 */
const STYLE_BY_VALUE: ReadonlyMap<string, CampaignStyleValue> = new Map(
  campaignStyleEnum.options.map((style) => [style, style])
);
const STATUS_BY_VALUE: ReadonlyMap<string, CampaignStatus> = new Map(
  campaignStatusEnum.options.map((status) => [status, status])
);

const GRAINS: readonly [CoopCountsGroup, string][] = [
  ['campaign', 'Campaign'],
  ['advertiser', 'Advertiser'],
];

const OFFER_TYPES: readonly [CampaignStyleValue, string][] = campaignStyleEnum.options.map(
  (style) => [style, campaignStyleEnumToLabel(style)]
);

/** One totals-row cell as text. Campaign-label columns carry no total; CPL is per-campaign only. */
function totalFor(totals: CoopCountsTotals, key: ColumnKey): string {
  switch (key) {
    case 'advertiser':
      return 'Total';
    case 'campaign':
      return '';
    case 'style':
      return '';
    case 'campaignDates':
      return '';
    case 'cpl':
      return '';
    case 'leadLimit':
      return '';
    case 'sent':
      return cell(totals.sent);
    case 'unique':
      return cell(totals.uniqueLeads);
    case 'rejected':
      return cell(totals.rejected);
    case 'revenue':
      return fmtMoney(totals.revenue);
    case 'status':
      return '';
    case 'accepted':
      return cell(totals.accepted);
    case 'duplicates':
      return cell(totals.duplicates);
    case 'errors':
      return cell(totals.errors);
    case 'skipped':
      return cell(totals.skipped);
    case 'exports':
      return '';
  }
}

/** Per-row export buttons — Unique first (her order). Hidden with the column on the advertiser grain. */
const EXPORT_VARIANTS: CoopLeadsVariant[] = ['unique', 'all'];
const EXPORT_LABELS: Record<CoopLeadsVariant, string> = { all: 'All', unique: 'Unique' };

function ExportButtons({
  row,
  onExport,
}: {
  row: CoopCountsRow;
  onExport: (row: CoopCountsRow, variant: CoopLeadsVariant) => void;
}) {
  return (
    <div className="flex justify-end gap-1">
      {EXPORT_VARIANTS.map((variant) => (
        <button
          key={variant}
          type="button"
          data-testid={`coop-export-${variant}-${row.campaignId ?? row.advertiserId}`}
          onClick={() => onExport(row, variant)}
          className="rounded-md px-1.5 py-0.5 text-xs font-medium text-accent hover:underline"
        >
          {EXPORT_LABELS[variant]}
        </button>
      ))}
    </div>
  );
}

/** The Campaign cell: the name opens the editor; the partner line rides beneath it. */
function CampaignCell({ row }: { row: CoopCountsRow }) {
  const navigate = useNavigate();
  const partner = row.partnerEndpointName ?? row.partnerUrl;
  return (
    <div className="max-w-[240px]">
      <button
        type="button"
        onClick={() => navigate(`/campaigns/${row.campaignId}`)}
        className="text-accent hover:underline"
      >
        {row.campaignName}
      </button>
      {partner && (
        <div className="truncate text-xs text-faint" title={partner}>
          {partner}
        </div>
      )}
    </div>
  );
}

/** The rich cells aside, every column renders through `cellFor`'s text map. */
function renderCell(
  row: CoopCountsRow,
  key: ColumnKey,
  onExport: (row: CoopCountsRow, variant: CoopLeadsVariant) => void
): ReactNode {
  switch (key) {
    case 'campaign':
      return <CampaignCell row={row} />;
    case 'exports':
      return <ExportButtons row={row} onExport={onExport} />;
    default:
      return cellFor(row, key);
  }
}

export function CoopCounts() {
  const range = useReportRange();
  const { from, to } = range;
  const [advertiserId, setAdvertiserId] = useState('');
  const [campaignId, setCampaignId] = useState('');
  // One view per style: the Co-op tab opens on Co-op rows; *All offer types* widens.
  const [campaignStyle, setCampaignStyle] = useState<CampaignStyleValue | ''>('co_op');
  // Status and test leads are shared across the style tabs (URL-backed, 087-01).
  const { campaignStatus, setCampaignStatus, includeTest, setIncludeTest } =
    useSharedReportFilters();
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState<CoopCountsGroup>('campaign');

  const [advertisers, setAdvertisers] = useState<AdvertiserResponse[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignDetailResponse[]>([]);
  const [data, setData] = useState<CoopCountsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filters: CoopCountsFilters = {
    from,
    to,
    advertiserId,
    campaignId,
    campaignStyle,
    campaignStatus,
    includeTest,
    group,
  };

  // Dropdown loaders — the shared typed client (best-effort: an empty dropdown
  // list degrades the filter, not the report).
  useEffect(() => {
    api
      .getAdvertisers()
      .then(setAdvertisers)
      .catch(() => {
        // Leave the previous (empty) list on error.
      });
    api
      .getCampaigns()
      .then(setCampaigns)
      .catch(() => {
        // Leave the previous (empty) list on error.
      });
  }, []);

  // Fetch on every filter change; the stale response of a superseded filter set
  // is dropped rather than rendered.
  useEffect(() => {
    if (!from || !to) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchCoopCounts({
      from,
      to,
      advertiserId,
      campaignId,
      campaignStyle,
      campaignStatus,
      includeTest,
      group,
    })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Request failed');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to, advertiserId, campaignId, campaignStyle, campaignStatus, includeTest, group]);

  // Co-op delivery surfaces only (README decision #11: Offer Type is one flat
  // field; the report never mixes in capture-journey campaigns), and only the
  // chosen advertiser's campaigns.
  const campaignOptions = useMemo(
    () =>
      campaigns.filter((c) => {
        if (advertiserId && c.advertiserId !== advertiserId) return false;
        return c.campaignStyle === 'co_op' || c.campaignStyle === 'custom_optin';
      }),
    [campaigns, advertiserId]
  );

  const columns = group === 'campaign' ? CAMPAIGN_COLUMNS : ADVERTISER_COLUMNS;

  // The search narrows the rows only — the footer keeps the server's totals (087-01 D5).
  const searchActive = search.trim() !== '';
  const searchedRows = useMemo(
    () =>
      (data?.rows ?? []).filter((row) =>
        matchesSearch(search, row.advertiserName, row.campaignName)
      ),
    [data, search]
  );

  const { sortedRows, sort, toggleSort } = useClientSort<CoopCountsRow, ColumnKey>(
    searchedRows,
    rowAccessor
  );

  const handleExport = () => {
    window.open(coopLeadsExportUrl({ ...filters, campaignId: filters.campaignId, variant: 'all' }));
  };

  const handleRowExport = (row: CoopCountsRow, variant: CoopLeadsVariant) => {
    window.open(coopLeadsExportUrl({ ...filters, campaignId: row.campaignId ?? '', variant }));
  };

  return (
    <ReportShell
      title="Co-op"
      desc="Billable sends per campaign — unique leads × CPL. Test leads hidden by default."
      actions={
        <Button data-testid="coop-export-csv" onClick={handleExport}>
          Export CSV
        </Button>
      }
    >
      <Card className="overflow-hidden">
        <div className="flex flex-wrap items-center gap-3 border-b border-line px-4 py-3">
          <RangeFilters
            preset={range.preset}
            from={range.from}
            to={range.to}
            applyPreset={range.applyPreset}
            setRange={range.setRange}
            testIdPrefix="coop"
          />

          <Typeahead<AdvertiserResponse>
            aria-label="Advertiser"
            options={advertisers}
            value={advertiserId || null}
            onChange={(v) => {
              setAdvertiserId(v ?? '');
              setCampaignId(''); // a campaign belongs to one advertiser — drop a now-hidden pick
            }}
            getId={(a) => a.id}
            getLabel={(a) => a.name}
            allOption={{ label: 'All advertisers' }}
            className="min-w-[170px]"
          />

          <Select
            aria-label="Campaign"
            value={campaignId}
            onChange={(e) => setCampaignId(e.target.value)}
            className="h-9 min-w-[180px]"
          >
            <option value="">All campaigns</option>
            {campaignOptions.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>

          <Select
            aria-label="Offer Type"
            value={campaignStyle}
            onChange={(e) => setCampaignStyle(STYLE_BY_VALUE.get(e.target.value) ?? '')}
            className="h-9 min-w-[150px]"
          >
            <option value="">All offer types</option>
            {OFFER_TYPES.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>

          <Select
            aria-label="Status"
            value={campaignStatus}
            onChange={(e) => setCampaignStatus(STATUS_BY_VALUE.get(e.target.value) ?? '')}
            className="h-9 min-w-[130px]"
          >
            <option value="">All statuses</option>
            {campaignStatusEnum.options.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </Select>

          <div className="flex gap-1">
            {GRAINS.map(([g, label]) => (
              <button
                key={g}
                type="button"
                data-testid={`coop-group-${g}`}
                onClick={() => setGroup(g)}
                className={`rounded-md px-2 py-0.5 text-xs font-medium transition ${
                  group === g
                    ? 'bg-accent text-white'
                    : 'bg-paper text-faint hover:text-ink hairline'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          <TestLeadsToggle checked={includeTest} onChange={setIncludeTest} />

          <ReportSearch
            value={search}
            onChange={setSearch}
            placeholder="Search advertiser or campaign"
            testId="coop-search"
          />

          {loading && <span className="text-xs text-muted-foreground">Loading…</span>}
        </div>

        <SearchNote
          active={searchActive}
          shown={sortedRows.length}
          total={data?.rows.length ?? 0}
          testId="coop-search-note"
        />

        {error && <ErrorBanner message={error} />}

        <div className="overflow-x-auto">
          <Table data-testid="coop-counts-table">
            <TableHeader>
              <TableRow>
                {columns.map((c) =>
                  c.key === 'exports' ? (
                    <TableHead key={c.key} className="text-right">
                      {c.label}
                    </TableHead>
                  ) : (
                    <SortableHeader
                      key={c.key}
                      label={c.label}
                      sortKey={c.key}
                      sort={sort}
                      onToggle={toggleSort}
                      title={c.help}
                    />
                  )
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data === null ? (
                <LoadingRow colSpan={columns.length} />
              ) : sortedRows.length === 0 ? (
                <EmptyRow
                  colSpan={columns.length}
                  message={
                    data.rows.length > 0 && searchActive
                      ? `No rows match "${search.trim()}".`
                      : 'No sends in this range.'
                  }
                />
              ) : (
                sortedRows.map((row) => (
                  <TableRow
                    key={
                      group === 'campaign' ? (row.campaignId ?? row.advertiserId) : row.advertiserId
                    }
                    data-testid={`coop-row-${group === 'campaign' ? (row.campaignId ?? row.advertiserId) : row.advertiserId}`}
                  >
                    {columns.map((c) => (
                      <TableCell
                        key={c.key}
                        className={c.numeric ? 'text-right tabular-nums' : undefined}
                        title={c.key === 'leadLimit' ? limitTypeLabel(row.limitType) : undefined}
                      >
                        {renderCell(row, c.key, handleRowExport)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
            {data !== null && (
              <TableFooter>
                <TableRow data-testid="coop-totals-row">
                  {columns.map((c) => (
                    <TableCell
                      key={c.key}
                      className={c.numeric ? 'text-right tabular-nums' : undefined}
                      title={c.key === 'unique' ? UNIQUE_TOTAL_TITLE : undefined}
                    >
                      {totalFor(data.totals, c.key)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </div>
      </Card>
    </ReportShell>
  );
}
